import React, { createContext, useContext, useState } from 'react';
import { ApprovalSortField, SortDirectionField } from '../graphql/generated/graphql';
import { ApprovalResponseData } from '../const/type';

interface ApprovalsContextType {
  page: number;
  rowsPerPage: number;
  totalCount: number;
  rows: ApprovalResponseData[];
  openDeleteModal: boolean;
  selected: readonly any[];
  status: string;
  searchKey: string;
  sort: ApprovalSortField;
  sortDirection: SortDirectionField;
  filter: any;
  filterId?: string;
  filterType: string;
  filterText: string;
  filters: Record<string, Record<string, string | Date | boolean>>;
  setPage: (page: number) => void;
  setRowsPerPage: (rowsPerPage: number) => void;
  setTotalCount: (count: number) => void;
  setRows: (rows: ApprovalResponseData[]) => void;
  setOpenDeleteModal: (open: boolean) => void;
  setSelected: (selected: readonly any[]) => void;
  setStatus: (status: string) => void;
  setSearchKey: (searchKey: string) => void;
  setSort: (sort: ApprovalSortField) => void;
  setSortDirection: (sortDirection: SortDirectionField) => void;
  setFilter: (filter: any) => void;
  setFilterId: (filterId?: string) => void;
  setFilterType: (filterType: string) => void;
  setFilterText: (filterText: string) => void;
  setFilters: (filters: Record<string, Record<string, string | Date | boolean>>) => void;
}

const ApprovalsContext = createContext<ApprovalsContextType | undefined>(undefined);

export const ApprovalsProvider: React.FC<{ children: any }> = ({ children }) => {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(100);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [rows, setRows] = useState<ApprovalResponseData[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [status, setStatus] = useState<string>('');
  const [searchKey, setSearchKey] = useState<string>('');
  const [sort, setSort] = useState<ApprovalSortField>(ApprovalSortField.id);
  const [sortDirection, setSortDirection] = useState<SortDirectionField>(SortDirectionField.Desc);
  const [filter, setFilter] = useState<any>({});
  const [filterId, setFilterId] = useState<string | undefined>(undefined);
  const [filterType, setFilterType] = useState<string>('equalTo');
  const [filterText, setFilterText] = useState<string>('');
  const [filters, setFilters] = useState<
    Record<string, Record<string, string | Date | boolean>>
  >({});

  return (
    <ApprovalsContext.Provider value={{
      page,
      rowsPerPage,
      totalCount,
      rows,
      openDeleteModal,
      selected,
      status,
      searchKey,
      sort,
      sortDirection,
      filter,
      filterId,
      filterType,
      filterText,
      filters,
      setPage,
      setRowsPerPage,
      setTotalCount,
      setRows,
      setOpenDeleteModal,
      setSelected,
      setStatus,
      setSearchKey,
      setSort,
      setSortDirection,
      setFilter,
      setFilterId,
      setFilterType,
      setFilterText,
      setFilters
    }}>
      {children}
    </ApprovalsContext.Provider>
  );
};

export const useApprovalsContext = (): ApprovalsContextType => {
  const context = useContext(ApprovalsContext);
  if (!context) throw new Error('useApprovalsContext must be used within a ApprovalsProvider');
  return context;
};
